import { z } from 'zod';
import { LIMITS, MAX_SCORE, MIN_SCORE } from './constants';
import { engineError, type EngineError } from './errors';

const id = z.string().min(1).max(64);
export const TeamIdSchema = z.enum(['A', 'B']);
export const AssignableRoleSchema = z.enum(['A', 'B', 'jury', 'none']);
export const TierSchema = z.enum(['S', 'A', 'B', 'C']);

/** Score 1–10 in 0.5 steps. */
export const ScoreSchema = z
  .number()
  .min(MIN_SCORE)
  .max(MAX_SCORE)
  .refine((v) => Number.isInteger(v * 2), { message: 'score must be a multiple of 0.5' });

/** C0/C1 controls, bidi overrides, zero-width chars and BOM: never allowed in display text. */
const UNSAFE_TEXT =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u001f\u007f-\u009f\u200b\u200e\u200f\u202a-\u202e\u2066-\u2069\ufeff]/;

/** Trimmed, NFC-normalized human-visible text with a length limit. */
const displayText = (max: number) =>
  z
    .string()
    .trim()
    .transform((v) => v.normalize('NFC'))
    .pipe(
      z
        .string()
        .min(1)
        .max(max)
        .refine((v) => !UNSAFE_TEXT.test(v), { message: 'invalid characters' }),
    );

export const PlayerProfileSchema = z.object({
  id,
  name: displayText(LIMITS.nameMax),
  avatar: z.string().min(1).max(16),
});

export const ItemSchema = z.object({
  id,
  name: z.string().min(1).max(80),
  emoji: z.string().min(1).max(16),
  tier: TierSchema.optional(),
  alcohol: z.boolean().optional(),
});

export const CategorySchema = z.object({
  id,
  name: z.string().min(1).max(30),
  emoji: z.string().min(1).max(16),
  items: z.array(ItemSchema),
  custom: z.boolean().optional(),
});

/** Partial settings update sent by the host. `rounds: 'auto'` re-enables auto-tracking. */
export const SettingsPatchSchema = z.object({
  rounds: z
    .union([z.number().int().min(LIMITS.minRounds).max(LIMITS.maxRounds), z.literal('auto')])
    .optional(),
  buyerOrder: z.enum(['ordered', 'random']).optional(),
  itemOrder: z.enum(['opener-picks', 'fixed', 'surprise']).optional(),
  roundCategories: z.array(id.nullable()).max(LIMITS.maxRounds).optional(),
  categoryPool: z.array(id).max(200).optional(),
  pace: z.enum(['normal', 'fast']).optional(),
  alcohol: z.boolean().optional(),
  joinMode: z.enum(['open', 'password', 'approval']).optional(),
  password: z.string().min(1).max(LIMITS.passwordMax).nullable().optional(),
  listed: z.boolean().optional(),
});
export type SettingsPatch = z.infer<typeof SettingsPatchSchema>;

/** Every client → server intent (the server adds `playerId`). */
export const IntentSchema = z.discriminatedUnion('type', [
  // lobby
  z.object({ type: z.literal('setRole'), role: AssignableRoleSchema }),
  z.object({ type: z.literal('moveMember'), targetId: id, role: AssignableRoleSchema }),
  z.object({
    type: z.literal('setTeamName'),
    team: TeamIdSchema,
    name: displayText(LIMITS.teamNameMax),
  }),
  z.object({
    type: z.literal('reorderTeam'),
    team: TeamIdSchema,
    order: z.array(id).max(LIMITS.maxTeamPlayers),
  }),
  z.object({ type: z.literal('kick'), targetId: id, ban: z.boolean().default(false) }),
  z.object({ type: z.literal('updateSettings'), patch: SettingsPatchSchema }),
  z.object({ type: z.literal('approveJoin'), targetId: id }),
  z.object({ type: z.literal('rejectJoin'), targetId: id }),
  z.object({ type: z.literal('start') }),
  // game
  z.object({ type: z.literal('vote'), itemId: id, score: ScoreSchema }),
  z.object({ type: z.literal('pickItem'), itemId: id }),
  z.object({ type: z.literal('bid'), amount: z.number().int().min(1).max(LIMITS.startPoints) }),
  z.object({ type: z.literal('pass') }),
  z.object({ type: z.literal('brokeDecide'), choice: z.enum(['take', 'give']) }),
  z.object({ type: z.literal('skipRoundResult') }),
  z.object({ type: z.literal('rematch'), shuffle: z.boolean() }),
]);

export type Intent = z.infer<typeof IntentSchema>;
export type IntentType = Intent['type'];

/** Validate an untrusted intent payload. */
export function parseIntent(
  raw: unknown,
): { ok: true; intent: Intent } | { ok: false; error: EngineError } {
  const res = IntentSchema.safeParse(raw);
  if (res.success) return { ok: true, intent: res.data };
  return { ok: false, error: engineError('INVALID_INTENT', res.error.issues[0]?.message) };
}
